<template>
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-7">
        <div class="card">
          <div class="card-header contact-card-header">
            <router-link
              to="/"
              class="contact-logo-link"
            >
              <b-img
                class="small-logo-image"
                :src="companyLogoImage"
                alt="App logo"
              />
            </router-link>
            <h4 class="mb-0">Contact us</h4>
            <p class="text-muted mb-0 mt-1 small">
              Send a message to the AGVA team. We typically reply by email.
            </p>
          </div>

          <div class="card-body">
            <form @submit.prevent="submit">
              <div class="form-group">
                <label for="contact_reason">Reason <span class="text-danger">*</span></label>
                <select
                  id="contact_reason"
                  v-model="form.reason"
                  class="form-control"
                  required
                >
                  <option
                    disabled
                    value=""
                  >
                    Select a reason
                  </option>
                  <option
                    v-for="reason in reasons"
                    :key="reason"
                    :value="reason"
                  >
                    {{ reason }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label for="contact_subject">Subject <span class="text-danger">*</span></label>
                <input
                  id="contact_subject"
                  v-model="form.subject"
                  type="text"
                  class="form-control"
                  maxlength="200"
                  required
                >
              </div>

              <div class="form-group">
                <label for="contact_email">Your email <span class="text-danger">*</span></label>
                <input
                  id="contact_email"
                  v-model="form.email"
                  type="email"
                  class="form-control"
                  required
                >
              </div>

              <div class="form-group">
                <label for="contact_phone">Phone number <span class="text-muted">(optional)</span></label>
                <input
                  id="contact_phone"
                  v-model="form.phone"
                  type="tel"
                  class="form-control"
                  maxlength="50"
                >
              </div>

              <div class="form-group">
                <label for="contact_message">Message <span class="text-danger">*</span></label>
                <textarea
                  id="contact_message"
                  v-model="form.message"
                  class="form-control"
                  rows="6"
                  maxlength="5000"
                  required
                />
              </div>

              <MathCaptchaField
                ref="contactCaptcha"
                input-id="contact_captcha"
                endpoint="api/contact/captcha"
                :captcha-token="form.captcha_token"
                :captcha-answer="form.captcha_answer"
                @update:captchaToken="form.captcha_token = $event"
                @update:captchaAnswer="form.captcha_answer = $event"
              />

              <button
                type="submit"
                class="btn btn-primary btn-block"
                :disabled="loading"
              >
                {{ loading ? 'Sending...' : 'Send message' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { BImg } from 'bootstrap-vue'
import { sweetError, sweetSuccess } from '@/libs/sweet-alerts'
import { $themeConfig } from '@themeConfig'
import MathCaptchaField from '@/views/Components/Elements/MathCaptchaField.vue'

const DEFAULT_REASONS = [
  'Sales / pricing inquiry',
  'Request a product demo',
  'Technical support',
  'Account or billing question',
  'Partnership or integration',
  'Feature request',
  'Report a bug',
  'Other',
]

export default {
  name: 'Contact',
  components: {
    BImg,
    MathCaptchaField,
  },
  data() {
    const { companyLogoImage } = $themeConfig.app
    return {
      companyLogoImage,
      loading: false,
      reasons: DEFAULT_REASONS,
      form: {
        reason: '',
        subject: '',
        email: '',
        phone: '',
        message: '',
        captcha_token: '',
        captcha_answer: '',
      },
    }
  },
  mounted() {
    this.loadReasons()
  },
  methods: {
    async loadReasons() {
      try {
        const { data } = await axios.get('api/contact/reasons')
        if (Array.isArray(data?.data) && data.data.length) {
          this.reasons = data.data
        }
      } catch (e) {
        // Keep default CRM reasons if the endpoint is unavailable.
      }
    },
    async submit() {
      if (!this.form.captcha_token) {
        sweetError('Error', 'Please refresh the question and try again.')
        return
      }

      this.loading = true
      try {
        await axios.get('sanctum/csrf-cookie')
        const { data } = await axios.post('api/contact', {
          reason: this.form.reason,
          subject: this.form.subject,
          email: this.form.email,
          phone: this.form.phone || null,
          message: this.form.message,
          captcha_token: this.form.captcha_token,
          captcha_answer: Number(this.form.captcha_answer),
        })

        sweetSuccess('Sent', data?.message || 'Your message has been sent.')
        this.form.reason = ''
        this.form.subject = ''
        this.form.email = ''
        this.form.phone = ''
        this.form.message = ''
        this.$refs.contactCaptcha?.refresh()
      } catch (e) {
        const responseData = e && e.response ? e.response.data : null
        if (responseData) {
          let errorMSG = (responseData.message || 'Could not send your message.') + '</br>'
          if (responseData.errors) {
            Object.keys(responseData.errors).forEach((errorKey) => {
              (responseData.errors[errorKey] || []).forEach((value) => {
                errorMSG += `${value}</br>`
              })
            })
          }
          sweetError('Error', errorMSG)
        } else {
          sweetError('Error', 'Could not send your message. Please try again.')
        }
        this.$refs.contactCaptcha?.refresh()
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.contact-card-header {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  display: flex;
  gap: 0.35rem;
}

.contact-logo-link {
  display: inline-flex;
}

.small-logo-image {
  max-height: 48px;
  width: auto;
}
</style>
